import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { connect } from 'react-redux';

import Global from '../../../store/global-selectors';
import { setRememberPrintSettings, setPrintDestination, closingPrintModal } from '../../../store/printing/print.actions';
import { allocateFulfillmentPlansFromPrintModal } from '../../../store/inventory/inventory.actions';
import { printDocumentRequest } from '../../../store/shipstation-connect/ss-connect.actions';
import { GuidService } from 'ui-components/services/guid-service';
import { DocumentService } from '../../../services/document-service';
import { DocumentType } from '../../../enums/document-type';
import { PrintModal } from './index';
import { PrintModalLoading } from './print-modal-loading';

const PrintModalWithLoading = ({ isLoading = false, ...rest }) => {
  if (isLoading) {
    return <PrintModalLoading />;
  }
  return <PrintModal {...rest} />;
};

PrintModalWithLoading.propTypes = {
  isLoading: PropTypes.bool,
};

const findInitialPrinter = (defaultPrintSetting, lastPrintDestination, allPrinters) => {
  const flattenedPrinters = allPrinters.reduce((acc, cur) => {
    return [...acc, ...cur.printers];
  }, []);

  if (defaultPrintSetting && !!flattenedPrinters.find((printer) => printer.id === defaultPrintSetting.id && !printer.disabled)) {
    return defaultPrintSetting;
  }

  if (
    lastPrintDestination &&
    !lastPrintDestination.disabled &&
    !!flattenedPrinters.find((printer) => printer.id === lastPrintDestination.id && !printer.disabled)
  ) {
    return lastPrintDestination;
  }

  const firstWorkStationWithActivePrinter = allPrinters.find((workStationGroupingOfPrinters) => {
    return !!workStationGroupingOfPrinters.printers.find((printer) => !printer.disabled);
  });

  if (!firstWorkStationWithActivePrinter) {
    return;
  }

  return firstWorkStationWithActivePrinter.printers.find((printer) => !printer.disabled);
};

const mapStateToProps = (state, props) => {
  const isLoading = Global.selectPrintIsLoading(state);
  if (isLoading) {
    return {
      isLoading,
    };
  }

  const documentType = get(props, 'documentType') || Global.selectPrintDocumentType(state) || 'label';
  const pdf = get(props, 'pdfFilePath') || Global.selectPrintPdfFilePath(state) || '';
  const fulfillmentPlanIds = get(props, 'fulfillmentPlanIds') || Global.selectPrintFulfillmentPlanIds(state);
  const fulfillmentIds = get(props, 'fulfillmentIds') || Global.selectPrintFulfillmentIds(state);
  const fulfillments = fulfillmentIds.reduce((fills, fulfillmentId) => {
    const fulfillment = Global.selectEntitiesFulfillmentById(state, {
      fulfillmentId,
    });
    if (fulfillment) {
      fills.push(fulfillment);
    }
    return fills;
  }, []);
  const isTestLabel = pdf.includes('testlabel');
  const lastPrintDestination = Global.selectPrintPrintDestination(state);
  const defaultPrintSetting = DocumentService.getDefaultPrintSettingForDocumentType(
    state,
    Object.values(DocumentType).find((docType) => docType.identifier === documentType),
    false,
  ); // possibly export Object.values(DocumentType) in enum file
  const printer = findInitialPrinter(defaultPrintSetting, lastPrintDestination, Global.selectConnectPrinters(state));

  return {
    documentToPrint: pdf,
    documentType,
    fulfillmentPlanIds,
    fulfillmentIds,
    fulfillments,
    isTestLabel,
    printer,
    rememberPrintSettings: Global.selectPrintRemeberPrintSettings(state),
  };
};

const getDocumentIds = (documentType, fulfillmentIds, fulfillmentPlanIds, fulfillmentPlanSetIds) => {
  if (documentType === DocumentType.Label.identifier) {
    return fulfillmentIds;
  }
  if (documentType === DocumentType.BatchPackingSlip.identifier) {
    return fulfillmentPlanSetIds;
  }
  return fulfillmentPlanIds;
};

const mapDispatchToProps = (dispatch, props) => {
  const {
    modalFrameworkMethods: { closeModal },
  } = props;
  return {
    modalFrameworkMethods: {
      ...props.modalFrameworkMethods,
      closeModal: () => {
        dispatch(closingPrintModal());
        closeModal();
      },
    },
    onPrintClick: (printer, documentType, fulfillmentPlanIds, fulfillmentIds, templateId, printUrl, isTestLabel, fulfillmentPlanSetIds) => {
      const requestId = GuidService.newGuid();
      const firstFulfillmentPlanId = parseInt(Array.isArray(fulfillmentPlanIds) ? fulfillmentPlanIds[0] : fulfillmentPlanIds, 10);
      const printJob = {
        requestId,
        printer,
        documentType,
        ids: getDocumentIds(documentType, fulfillmentIds, fulfillmentPlanIds, fulfillmentPlanSetIds),
        fulfillmentPlanIds,
        templateId,
        printUrl,
        testOrderId: isTestLabel ? firstFulfillmentPlanId : undefined,
      };
      dispatch(printDocumentRequest(printJob));
    },
    setRememberPrintSettings: (rememberPrintSettings) => dispatch(setRememberPrintSettings(rememberPrintSettings)),
    allocateFulfillmentPlans: (fulfillmentPlanIds, onSuccess) => {
      dispatch(allocateFulfillmentPlansFromPrintModal(fulfillmentPlanIds, onSuccess));
    },
    setPrintDestination: (selected) => {
      dispatch(setPrintDestination(selected));
    },
  };
};

export const PrintModalContainer = connect(mapStateToProps, mapDispatchToProps)(PrintModalWithLoading);
